import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context";
import { Chapter, Reveal } from "@/components/Reveal";
import { SERVICE_SLUGS } from "@/servicesData";

const SPANS = [
  "md:col-span-6",
  "md:col-span-6",
  "",
  "md:col-span-6",
  "md:col-span-6",
  "md:col-span-6",
  "md:col-span-6",
];

export default function Services() {
  const { t } = useLang();
  const navigate = useNavigate();

  const pick = (i) => {
    navigate(`/services/${SERVICE_SLUGS[i]}`);
  };

  return (
    <section id="services" data-testid="services-section" className="py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <Chapter number={t.services.chapter} label={t.services.label} />

        <div className="grid grid-cols-12 gap-y-6 lg:gap-10 items-end mb-16 sm:mb-20">
          <Reveal className="col-span-12 lg:col-span-7 min-w-0">
            <h2
              data-testid="services-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal font-medium leading-[1.1]"
            >
              {t.services.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5 min-w-0">
            <p
              data-testid="services-intro"
              className="text-sm sm:text-base text-ink/70 leading-relaxed font-light lg:pb-2"
            >
              {t.services.intro}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-5">
          {t.services.items.map((s, i) => {
            const dark = s.key === "travel";
            return (
              <Reveal key={s.num} delay={(i % 2) * 0.07} className={`col-span-12 ${SPANS[i]}`}>
                <button
                  data-testid={`service-box-${s.num}`}
                  onClick={() => pick(i)}
                  className={`group relative h-full w-full text-left overflow-hidden transition-colors duration-500 border ${
                    dark
                      ? "bg-charcoal border-charcoal p-9 sm:p-12 lg:p-16"
                      : "bg-cream/50 border-charcoal/15 hover:border-gold/60 p-9 sm:p-12"
                  }`}
                >
                  <span className="absolute top-0 left-0 h-px w-0 bg-gold transition-all duration-700 group-hover:w-full" />

                  <span
                    aria-hidden="true"
                    className={`absolute -top-5 right-2 sm:-top-7 sm:right-6 font-serif text-[7.5rem] sm:text-[10rem] leading-none font-light select-none transition-all duration-700 group-hover:-translate-y-2 ${
                      dark
                        ? "text-ivory/[0.07] group-hover:text-ivory/[0.12]"
                        : "text-gold/25 group-hover:text-gold/40"
                    }`}
                  >
                    {s.num}
                  </span>

                  {dark && (
                    <span className="absolute bottom-9 right-9 sm:bottom-12 sm:right-12 text-[10px] uppercase tracking-[0.28em] text-gold">
                      {t.services.keyService}
                    </span>
                  )}

                  <div className="relative">
                    <h3
                      className={`font-serif text-2xl sm:text-3xl lg:text-4xl font-medium uppercase tracking-[0.05em] leading-[1.15] max-w-md ${
                        dark ? "text-ivory" : "text-charcoal"
                      }`}
                    >
                      {s.title}
                    </h3>
                    <p
                      className={`mt-5 max-w-md text-sm sm:text-base leading-relaxed font-light transition-all duration-500 ${
                        dark
                          ? "text-taupe opacity-75 group-hover:opacity-100"
                          : "text-ink/60 opacity-75 group-hover:opacity-100 group-hover:text-ink/85"
                      }`}
                    >
                      {s.desc}
                    </p>
                    {s.tagline && (
                      <p className="mt-6 max-w-md text-xs uppercase tracking-[0.22em] text-gold leading-relaxed">
                        {s.tagline}
                      </p>
                    )}
                    <span
                      className={`mt-9 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] opacity-0 -translate-x-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 ${
                        dark ? "text-gold" : "text-charcoal"
                      }`}
                    >
                      {t.services.explore} <ArrowRight size={13} />
                    </span>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
